import moment from "moment";
import { Patient } from "../types/appointment";

export function getPatientNameText(patient: Patient): string {
  return [patient.fn, patient.mn, patient.ln].filter(Boolean).join(" ");
}

export function addAlpha(color: string, opacity: number): string {
  // coerce values so ti is between 0 and 1.
  const _opacity = Math.round(Math.min(Math.max(opacity || 1, 0), 1) * 255);
  return color + _opacity.toString(16).toUpperCase();
}

export const calculateAge = (
  dob: string,
): { age: string; month: string; day: string } => {
  const today = moment(new Date());
  const dobInMoment = moment(dob);
  const years = today.diff(dobInMoment, 'year');
  dobInMoment.add(years, 'years');
  const months = today.diff(dobInMoment, 'months');
  dobInMoment.add(months, 'months');
  const days = today.diff(dobInMoment, 'days');

  return { age: years.toString(), month: months.toString(), day: days.toString() };
};

const parseAge = (
  a: {
    age?: string | null;
    month?: string | null;
    dob?: string | null;
    ageInYear?: string | null;
    ageInMonths?: string | null;
  }
) => {
  let aa, am, ad;

  if (a.dob) {
    const temp = calculateAge(a.dob);
    aa = temp.age;
    am = temp.month;
    ad = temp.day;
  } else {
    aa = a.age || a.ageInYear;
    am = a.month || a.ageInMonths;
  }

  const ageInt = parseInt(aa || '0');
  const ageMonthInt = parseInt(am || '0');
  const ageDayInt = parseInt(ad || '0');

  return { aa, am, ad, ageInt, ageMonthInt, ageDayInt };
};

export const getAgeString = (
  a: { age?: string; month?: string; dob?: string | null },
  prefix?: string,
  suffix?: string
): string => {
  const { aa, am, ad, ageInt, ageMonthInt, ageDayInt } = parseAge(a);

  if (isNaN(ageInt) && isNaN(ageMonthInt)) {
    return '';
  }

  if (aa && am && ageInt > 0 && ageMonthInt > 0) {
    if (ageInt < 10 && ad && ageDayInt > 0) {
      return `${prefix || ''}${ageInt}y, ${ageMonthInt}m, ${ageDayInt}d${suffix || ''}`;
    }
    return `${prefix || ''}${ageInt}y, ${ageMonthInt}m${suffix || ''}`;
  } else if (aa && ageInt > 0) {
    if (ageInt < 10 && (am || ad) && (ageMonthInt > 0 || ageDayInt > 0)) {
      const parts = [`${ageInt}y`];
      if (ageMonthInt > 0) parts.push(`${ageMonthInt}m`);
      if (ad && ageDayInt > 0) parts.push(`${ageDayInt}d`);
      return `${prefix || ''}${parts.join(', ')}${suffix || ''}`;
    }
    return `${prefix || ''}${ageInt}y${suffix || ''}`;
  } else if (am && ageMonthInt > 0) {
    if (ageMonthInt > 11) {
      return getAgeString(
        { age: `${ageMonthInt / 12}`, month: `${ageMonthInt % 12}` },
        prefix,
        suffix,
      );
    }
    return `${prefix || ''}${ageMonthInt}m${ad && ageDayInt > 0 ? `, ${ageDayInt}d` : ''}${suffix || ''}`;
  } else if (ad && ageDayInt > 0) {
    return `${prefix || ''}${ageDayInt}d${suffix || ''}`;
  }

  return '';
};

export const caclculateAgeOfPatientFromDob = (dob: string): string | undefined => {
  if (!dob) return undefined;

  const today = moment().startOf("day");
  const dobMoment = moment(dob, moment.ISO_8601, true);

  if (!dobMoment.isValid()) return undefined;

  if (dobMoment.isAfter(today)) {
    return "0D";
  }

  const years = today.diff(dobMoment, "years");

  if (years > 0) {
    return `${years}Y`;
  }

  const months = today.diff(dobMoment, "months");

  if (months > 0) {
    return `${months}M`;
  }

  const days = today.diff(dobMoment, "days");

  return `${days}D`;
};